import { FormEvent, useRef, useState } from "react";
import {
  ChatWidgetText,
  ChatWidgetValues,
  FeatureRequestItem,
  FeatureRequestsWidget,
  isSafeWidgetUrl,
} from "../../../Helper/ChatWidget";
import {
  AlertIcon,
  ArrowUpRightIcon,
  CheckIcon,
  ChevronUpIcon,
  LightbulbIcon,
} from "../ChatWidgetIcons";
import {
  Button,
  Card,
  ChatWidgetAnswer,
  cx,
  Foot,
  openWidgetUrl,
  Pill,
  Receipt,
} from "../ChatWidgetPrimitives";
import "./FeatureRequestsCard.scss";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const isValidIntakeEmail = (value: string) =>
  EMAIL_PATTERN.test(value.trim());

const voteCount = (count: number | undefined, text: ChatWidgetText) => {
  const n = typeof count === "number" && count > 0 ? count : 0;
  return n === 1
    ? text("widgetVotesOne")
    : text("widgetVotes", { n: n.toLocaleString() });
};

/**
 * The inline email ask for an anonymous session: a vote is a subscription,
 * and both are keyed by email (PLAN §1.4).
 */
const EmailField = ({
  id,
  value,
  onChange,
  showError,
  inputRef,
  text,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  showError: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  text: ChatWidgetText;
}) => (
  <div className="cw-fr-email">
    <div className={cx("cw-fr-email-box", showError && "cw-fr-email-box--error")}>
      <label htmlFor={id}>{text("widgetEmail")}</label>
      <input
        ref={inputRef}
        id={id}
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value}
        placeholder="name@company.com"
        aria-invalid={showError || undefined}
        aria-describedby={`${id}-hint`}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
    <div
      id={`${id}-hint`}
      className={cx("cw-fr-email-hint", showError && "cw-fr-email-hint--error")}
      role={showError ? "alert" : undefined}
    >
      {showError ? text("widgetEmailInvalid") : text("widgetEmailHint")}
    </div>
  </div>
);

/** A linked title whose ↗ never wraps onto a line of its own (it keeps the last word). */
const LinkTitle = ({ title }: { title: string }) => {
  const cut = title.trimEnd().lastIndexOf(" ");
  const head = cut === -1 ? "" : title.slice(0, cut + 1);
  const tail = cut === -1 ? title : title.slice(cut + 1);
  return (
    <>
      {head}
      <span className="cw-fr-title-tail">
        {tail}
        <ArrowUpRightIcon size={12} />
      </span>
    </>
  );
};

const RequestRow = ({
  item,
  isVoted,
  isPicked,
  isDimmed,
  isLocked,
  countBump,
  onVote,
  text,
}: {
  item: FeatureRequestItem;
  isVoted: boolean;
  isPicked: boolean;
  isDimmed: boolean;
  isLocked: boolean;
  countBump: number;
  onVote: () => void;
  text: ChatWidgetText;
}) => {
  const hasLink = isSafeWidgetUrl(item.url);
  return (
    <li
      className={cx(
        "cw-fr-row",
        isPicked && "cw-fr-row--on",
        isDimmed && "cw-fr-row--dim",
      )}
    >
      <div className="cw-stack cw-grow">
        {hasLink ? (
          <button
            type="button"
            className="cw-fr-title cw-fr-title--link"
            onClick={() => openWidgetUrl(item.url)}
          >
            <LinkTitle title={item.title} />
          </button>
        ) : (
          <span className="cw-fr-title">{item.title}</span>
        )}
        <span className="cw-fr-meta">
          <Pill
            status={
              item.statusLabel
                ? { label: item.statusLabel, tone: item.statusTone }
                : undefined
            }
            withDot={false}
            className="cw-fr-pill"
          />
          <span className="cw-num">
            {voteCount((item.upvotesCount ?? 0) + countBump, text)}
          </span>
        </span>
      </div>
      {isVoted ? (
        <span className="cw-fr-vote cw-fr-vote--done" role="status">
          <CheckIcon size={14} strokeWidth={2.8} />
          {text("widgetVoted")}
        </span>
      ) : (
        !isLocked && (
          <button
            type="button"
            className="cw-fr-vote"
            aria-label={text("widgetUpvoteItem", { name: item.title })}
            onClick={onVote}
          >
            <ChevronUpIcon size={14} />
            {text("widgetUpvote")}
          </button>
        )
      )}
    </li>
  );
};

/**
 * Kai intake: feature requests (PLAN §7). `choose` lists up to three public
 * requests with Upvote ("Voted ✓" after) and "None of these"; `proposed`
 * shows the request Kai would file; `created` / `recorded` / `upvoted` /
 * `dismissed` / `error` are what came of it. Anonymous sessions type their
 * email in the card first. Once answered the card is frozen; only the server
 * moves `state`.
 */
export const FeatureRequestsCard = ({
  widget,
  onAnswer,
  text,
}: {
  widget: FeatureRequestsWidget;
  onAnswer: ChatWidgetAnswer;
  text: ChatWidgetText;
}) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const state = widget.state ?? "choose";
  const items = (widget.items ?? []).slice(0, 3);
  const response = widget.response;
  const needsEmail = !!widget.emailRequired && !response;
  const emailId = `cw-${widget.id}-email`;

  /** The email (when asked for), or undefined after pointing at the field. */
  const withEmail = (values: ChatWidgetValues): ChatWidgetValues | undefined => {
    if (!needsEmail) return values;
    if (!isValidIntakeEmail(email)) {
      setEmailError(true);
      emailRef.current?.focus();
      return undefined;
    }
    return { ...values, email: email.trim() };
  };

  const vote = (item: FeatureRequestItem) => {
    const values = withEmail({ selected: [item.id] });
    if (!values) return;
    onAnswer(
      "select",
      values,
      text("widgetUpvotedLabel", { name: item.title }),
    );
  };

  const emailField = needsEmail ? (
    <EmailField
      id={emailId}
      value={email}
      onChange={(value) => {
        setEmail(value);
        if (emailError && isValidIntakeEmail(value)) setEmailError(false);
      }}
      showError={emailError}
      inputRef={emailRef}
      text={text}
    />
  ) : null;

  if (state === "dismissed" || (state === "choose" && response?.action === "cancel")) {
    return (
      <Receipt
        tone="neutral"
        title={text("widgetNoneFit")}
        detail={text("widgetNoneFitDetail")}
      />
    );
  }

  if (state === "recorded") {
    return (
      <Receipt
        title={text("widgetVoteRecorded")}
        detail={text("widgetVoteRecordedDetail")}
      />
    );
  }

  if (state === "error") {
    return (
      <Card className="cw-fr cw-fr--error">
        <div className="cw-fr-notice" role="alert">
          <AlertIcon size={16} />
          <span>{widget.message || text("widgetRequestFailed")}</span>
        </div>
      </Card>
    );
  }

  if (state === "created") {
    const created = widget.created ?? widget.proposal;
    return (
      <Card className="cw-fr cw-fr--created" label={text("widgetRequestSubmitted")}>
        <div className="cw-fr-created" role="status">
          <span className="cw-receipt-tile cw-receipt-tile--success" aria-hidden="true">
            <CheckIcon size={15} strokeWidth={2.8} />
          </span>
          <span className="cw-stack cw-grow">
            <span className="cw-receipt-title">
              {text("widgetRequestSubmitted")}
            </span>
            <span className="cw-receipt-detail">
              {text("widgetRequestSubmittedDetail")}
            </span>
          </span>
        </div>
        {created?.title && (
          <div className="cw-fr-created-item">
            {isSafeWidgetUrl(widget.created?.url) ? (
              <button
                type="button"
                className="cw-fr-title cw-fr-title--link"
                onClick={() => openWidgetUrl(widget.created?.url)}
              >
                <LinkTitle title={created.title} />
              </button>
            ) : (
              <span className="cw-fr-title">{created.title}</span>
            )}
            {widget.created?.statusLabel && (
              <Pill
                status={{ label: widget.created.statusLabel, tone: "neutral" }}
                withDot={false}
                className="cw-fr-pill"
              />
            )}
          </div>
        )}
      </Card>
    );
  }

  if (state === "proposed") {
    const proposal = widget.proposal;
    const isCancelled = response?.action === "cancel";
    const isSubmitted = response?.action === "submit";
    if (isCancelled) {
      return (
        <Receipt
          tone="neutral"
          title={text("widgetNotNow")}
          detail={proposal?.title}
          isDetailStruck
        />
      );
    }
    const submit = (event: FormEvent) => {
      event.preventDefault();
      if (!proposal) return;
      // Unedited: the server files the proposal as it stands (§5.2).
      const values = withEmail({});
      if (!values) return;
      onAnswer("submit", values, text("widgetSubmitRequestLabel"));
    };
    return (
      <Card className="cw-fr cw-fr--proposed" label={text("widgetNewRequest")}>
        <form onSubmit={submit} noValidate>
          <div className="cw-fr-proposal">
            <span className="cw-fr-proposal-tile" aria-hidden="true">
              <LightbulbIcon size={18} />
            </span>
            <span className="cw-stack cw-grow">
              <span className="cw-eyebrow">{text("widgetNewRequest")}</span>
              <span className="cw-h cw-fr-proposal-title">
                {proposal?.title}
              </span>
            </span>
          </div>
          {proposal?.description && (
            <p className="cw-body cw-fr-proposal-desc">{proposal.description}</p>
          )}
          {emailField}
          {isSubmitted ? (
            <div className="cw-fr-pending" role="status">
              <span className="cw-spinner" aria-hidden="true" />
              <span>{text("widgetSubmitting")}</span>
            </div>
          ) : (
            <Foot>
              <Button
                variant="ghost"
                onClick={() => onAnswer("cancel", {}, text("widgetNotNow"))}
              >
                {text("widgetNotNow")}
              </Button>
              <Button type="submit" disabled={!proposal?.title}>
                {text("widgetSubmitRequest")}
              </Button>
            </Foot>
          )}
        </form>
      </Card>
    );
  }

  // choose / upvoted: the list. Answered (or upvoted) = the pick marked, the
  // rest dimmed, nothing left to tap.
  const pickedId =
    state === "upvoted"
      ? widget.selectedId
      : response?.action === "select" && Array.isArray(response.values?.selected)
        ? response.values.selected[0]
        : undefined;
  const isFrozen = !!response || state === "upvoted";
  const title = widget.title ?? text("widgetSimilarRequests");

  return (
    <Card className="cw-fr" label={title}>
      <div className="cw-fr-head">
        <span className="cw-h">{title}</span>
        {!isFrozen && (
          <span className="cw-muted cw-sm">
            {widget.subtitle ?? text("widgetSimilarRequestsHint")}
          </span>
        )}
      </div>
      <ul className="cw-fr-list">
        {items.map((item) => {
          const isPicked = item.id === pickedId;
          return (
            <RequestRow
              key={item.id}
              item={item}
              isPicked={isPicked}
              isVoted={isPicked || !!item.upvoted}
              isDimmed={isFrozen && !isPicked}
              isLocked={isFrozen}
              // The server counts the vote on `upvoted`; until then, the
              // customer's own tap shows in the count right away.
              countBump={isPicked && state === "choose" && !item.upvoted ? 1 : 0}
              onVote={() => vote(item)}
              text={text}
            />
          );
        })}
      </ul>
      {isFrozen ? (
        pickedId && (
          <div className="cw-fr-foot-note" role="status">
            {text("widgetUpvotedDetail")}
          </div>
        )
      ) : (
        <>
          {emailField}
          <Foot>
            <Button
              variant="ghost"
              onClick={() => onAnswer("cancel", {}, text("widgetNoneOfThese"))}
            >
              {text("widgetNoneOfThese")}
            </Button>
          </Foot>
        </>
      )}
    </Card>
  );
};
